import { auth } from '../firebase';
import { supabase } from '../supabaseClient';
import { safeStorage } from '../utils/safeStorage';

const FAVORITES_KEY_PREFIX = 'user_favorites';
const GUEST_SCOPE = 'guest';

class FavoritesService {
    private favorites: Set<string> = new Set();
    private listeners: Set<() => void> = new Set();
    private hydratedUsers: Set<string> = new Set();
    private activeStorageKey = '';

    constructor() {
        this.loadFavoritesForKey(this.getStorageKey());
    }

    private getStorageKey(userId: string | null = auth.currentUser?.uid ?? null): string {
        return `${FAVORITES_KEY_PREFIX}:${userId || GUEST_SCOPE}`;
    }

    private loadFavoritesForKey(storageKey: string) {
        try {
            this.activeStorageKey = storageKey;
            const stored = safeStorage.getItem(storageKey);
            if (stored) {
                const parsed = JSON.parse(stored);
                this.favorites = new Set(parsed);
                return;
            }

            this.favorites = new Set();
        } catch (e) {
            console.error('Failed to load favorites:', e);
            this.favorites = new Set();
        }
    }

    private ensureScopedFavorites() {
        const expectedKey = this.getStorageKey();
        if (expectedKey !== this.activeStorageKey) {
            this.loadFavoritesForKey(expectedKey);
        }
    }

    // Save favorites to localStorage
    private saveFavorites() {
        try {
            this.ensureScopedFavorites();
            const array = Array.from(this.favorites);
            safeStorage.setItem(this.activeStorageKey, JSON.stringify(array));
            this.notifyListeners();
        } catch (e) {
            console.error('Failed to save favorites:', e);
        }
    }

    async hydrateForCurrentUser(): Promise<void> {
        this.ensureScopedFavorites();

        const user = auth.currentUser;
        if (!user) {
            this.notifyListeners();
            return;
        }

        if (!this.hydratedUsers.has(user.uid)) {
            await this.loadFromFirebase();
            return;
        }

        this.notifyListeners();
    }

    // Add a template to favorites
    addFavorite(templateId: string): void {
        this.ensureScopedFavorites();
        this.favorites.add(templateId);
        this.saveFavorites();
        this.syncToFirebase();
    }

    // Remove a template from favorites
    removeFavorite(templateId: string): void {
        this.ensureScopedFavorites();
        this.favorites.delete(templateId);
        this.saveFavorites();
        this.syncToFirebase();
    }

    // Toggle favorite status
    toggleFavorite(templateId: string): boolean {
        if (this.favorites.has(templateId)) {
            this.removeFavorite(templateId);
            return false;
        } else {
            this.addFavorite(templateId);
            return true;
        }
    }

    // Check if a template is favorited
    isFavorite(templateId: string): boolean {
        this.ensureScopedFavorites();
        return this.favorites.has(templateId);
    }

    // Get all favorite template IDs
    getFavorites(): string[] {
        this.ensureScopedFavorites();
        return Array.from(this.favorites);
    }

    // Get count of favorites
    getCount(): number {
        this.ensureScopedFavorites();
        return this.favorites.size;
    }

    // Subscribe to favorites changes
    subscribe(listener: () => void): () => void {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }

    // Notify all listeners
    private notifyListeners() {
        this.listeners.forEach(listener => listener());
    }

    // Sync favorites to Supabase (if user is logged in)
    private async syncToFirebase() {
        const user = auth.currentUser;
        if (!user) return;

        try {
            this.ensureScopedFavorites();
            if (!this.hydratedUsers.has(user.uid)) {
                await this.loadFromFirebase();
            }

            await supabase
                .from('users')
                .upsert({
                    id: user.uid,
                    favorites: this.getFavorites()
                }, {
                    onConflict: 'id'
                });
        } catch (e) {
            console.error('Failed to sync favorites to Supabase:', e);
        }
    }

    // Load favorites from Supabase
    async loadFromFirebase(): Promise<void> {
        const user = auth.currentUser;
        if (!user) return;

        try {
            this.loadFavoritesForKey(this.getStorageKey(user.uid));
            const { data, error } = await supabase
                .from('users')
                .select('favorites')
                .eq('id', user.uid)
                .single();

            if (!error) {
                const remoteFavorites = Array.isArray(data?.favorites) ? data.favorites : [];
                this.favorites = new Set([...this.favorites, ...remoteFavorites]);
                this.hydratedUsers.add(user.uid);
                this.saveFavorites();
            }
        } catch (e) {
            console.error('Failed to load favorites from Supabase:', e);
        }
    }

    // Clear all favorites
    clearAll(): void {
        this.ensureScopedFavorites();
        this.favorites.clear();
        this.saveFavorites();
        this.syncToFirebase();
    }
}

export const favoritesService = new FavoritesService();
